import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Card,
  Typography,
  Input,
  Select,
  Row,
  Col,
  Tag,
  Alert,
  Button,
  Space,
  Modal,
  Descriptions,
  Tooltip,
  DatePicker,
  Dropdown,
  message,
} from "antd";
import { useSelector } from "react-redux";
import {
  getReclamations,
  updateReclamationStatus,
  replyToReclamation,
} from "../../api/parkpass/parkpassReclamations";
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  EditOutlined,
  InfoCircleOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const statusColors = {
  submitted: "blue",
  in_progress: "orange",
  resolved: "green",
  rejected: "red",
  closed: "gray",
};

const statusLabels = {
  submitted: "Soumis",
  in_progress: "En cours",
  resolved: "Résolu",
  rejected: "Rejeté",
  closed: "Fermé",
};

const reclamationTypeLabels = {
  infraction: "Infraction",
  subscription: "Abonnement",
  other: "Autre",
};

const CitizenReclamationsPage = () => {
  const [reclamations, setReclamations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedReclamation, setSelectedReclamation] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: null,
    reclamation_type: null,
  });
  const [dateRange, setDateRange] = useState([]);
  const [exporting, setExporting] = useState(false);

  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  const user = useSelector((state) => state.user.user);

  // Day-granular bounds: the backend widens `date_to` to the end of that day.
  const buildDateParams = (range = dateRange) => ({
    date_from: range?.[0] ? range[0].format("YYYY-MM-DD") : undefined,
    date_to: range?.[1] ? range[1].format("YYYY-MM-DD") : undefined,
  });

  const fetchReclamations = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    currentFilters = filters,
    currentDateRange = dateRange
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getReclamations({
        page,
        pageSize,
        ...currentFilters,
        ...buildDateParams(currentDateRange),
      });
      setReclamations(response.data.items || response.data);
      setPagination({
        current: response.data.page || 1,
        pageSize: response.data.page_size || pageSize,
        total: response.data.total ?? response.data.length,
      });
    } catch (err) {
      setError("Échec du chargement des réclamations. Veuillez réessayer plus tard.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReclamations();
    // Fetch once on mount only — every other fetch is triggered explicitly by
    // handlers below. Depending on `fetchReclamations` here would re-run this
    // effect on every pagination/filter change (it's a new function each
    // render), double-firing the request that handler already issued.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    workerPdfRef.current = new Worker(
      new URL("../../workers/pdfWorker.js", import.meta.url)
    );
    workerExcelRef.current = new Worker(
      new URL("../../workers/excelWorker.js", import.meta.url)
    );

    workerPdfRef.current.onmessage = (e) => {
      const { status, blobURL, operation, filename, message: msg } = e.data;
      switch (status) {
        case "complete":
          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }
          setExporting(false);
          break;
        case "error":
          message.error(`Erreur lors de la génération du PDF: ${msg}`);
          setExporting(false);
          break;
        default:
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error: workerError } = e.data;
      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
      } else {
        message.error(
          `Erreur lors de la génération du fichier Excel: ${workerError}`
        );
      }
      setExporting(false);
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
  }, []);

  const handleTableChange = (newPagination) => {
    fetchReclamations(newPagination.current, newPagination.pageSize, filters);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    fetchReclamations(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    const resetFilters = {
      status: null,
      reclamation_type: null,
    };
    setFilters(resetFilters);
    setDateRange([]);
    // Pass the cleared range explicitly: setDateRange([]) won't be visible via
    // the `dateRange` closure until the next render.
    fetchReclamations(1, pagination.pageSize, resetFilters, []);
  };

  // --- Export (Web Worker-based, same skeleton as CitizenTransactionsPage) ---

  const formatCellDate = (date) =>
    date ? new Date(date).toLocaleString("fr-FR") : "";

  /** Fetch every reclamation matching the current filters, replies included. */
  const fetchExportData = async () => {
    try {
      const response = await getReclamations({
        ...filters,
        ...buildDateParams(),
        no_pagination: true,
      });
      return response.data.items || response.data || [];
    } catch (err) {
      console.error("Error fetching export data:", err);
      message.error("Erreur lors de la récupération des données d'export");
      return null;
    }
  };

  const buildExportRows = (records) =>
    records.map((rec) => ({
      id: rec.id,
      reclamation_type:
        reclamationTypeLabels[rec.reclamation_type] || rec.reclamation_type || "",
      subject: rec.subject || "",
      message: rec.message || "",
      status: statusLabels[rec.status] || rec.status || "",
      full_name: `${rec.first_name || ""} ${rec.last_name || ""}`.trim() || "-",
      email: rec.email || "",
      phone: rec.phone || "-",
      admin_reply: rec.admin_reply || "-",
      replied_at: formatCellDate(rec.replied_at) || "-",
      created_at: formatCellDate(rec.created_at),
    }));

  // `width` (percentage of the page) keeps the table inside the A4 page:
  // pdfmake's default 'auto' sizing fits a column to its widest single
  // unwrapped line, which is what was pushing Réponse apportée/Répondu
  // le/Créé le off the page. Percentages always sum to the page width — a
  // literal point width would not, since pdfmake adds each column's own
  // padding/border on top of it. With an explicit width, long columns
  // (Sujet, Message, Réponse apportée) wrap instead of stretching the table
  // past the page. These sum to 100%.
  const exportHeaders = [
    { text: "ID", type: "string", key: "id" },
    { text: "Type", type: "string", key: "reclamation_type", width: "7%" },
    { text: "Sujet", type: "string", key: "subject", width: "9%" },
    { text: "Message", type: "string", key: "message", width: "13%" },
    { text: "Statut", type: "string", key: "status", width: "6%" },
    { text: "Client", type: "string", key: "full_name", width: "9%" },
    { text: "Email", type: "string", key: "email", width: "11%" },
    { text: "Téléphone", type: "string", key: "phone", width: "8%" },
    {
      text: "Réponse apportée",
      type: "string",
      key: "admin_reply",
      width: "13%",
    },
    { text: "Répondu le", type: "string", key: "replied_at", width: "10%" },
    { text: "Créé le", type: "string", key: "created_at", width: "11%" },
  ];

  const buildExportPayload = (records) => [
    [
      "Réclamations Citizen Wallet",
      "",
      `Date à partir de : ${dateRange?.[0]?.format("YYYY-MM-DD") || "N/A"}`,
      `Date jusqu'au : ${dateRange?.[1]?.format("YYYY-MM-DD") || "N/A"}`,
    ],
    [],
    [],
    user?.full_name || "Rapport d'exportation",
    [],
    [exportHeaders],
    [buildExportRows(records)],
    window.location.origin,
  ];

  const handleExcelClick = async () => {
    setExporting(true);
    const records = await fetchExportData();
    if (!records) {
      setExporting(false);
      return;
    }
    const timestamp = new Date().toISOString().replace(/:/g, "-");
    workerExcelRef.current.postMessage({
      data: buildExportPayload(records),
      filename: `reclamations_${timestamp}.xlsx`,
    });
  };

  const handlePdfClick = async (operation) => {
    setExporting(true);
    const records = await fetchExportData();
    if (!records) {
      setExporting(false);
      return;
    }
    const timestamp = new Date().toISOString().replace(/:/g, "-");
    workerPdfRef.current.postMessage({
      data: buildExportPayload(records),
      operation,
      filename: `reclamations_${timestamp}.pdf`,
    });
  };

  const pdfMenuItems = [
    {
      key: "download",
      label: "Télécharger",
      onClick: () => handlePdfClick("download"),
      disabled: exporting,
    },
    {
      key: "open",
      label: "Ouvrir",
      onClick: () => handlePdfClick("open"),
      disabled: exporting,
    },
  ];

  // --- End Export ---

  const handleViewDetail = (record) => {
    setSelectedReclamation(record);
    setDetailModalVisible(true);
  };

  const handleCloseDetail = () => {
    setDetailModalVisible(false);
    setSelectedReclamation(null);
  };

  const handleOpenStatusModal = (record) => {
    setSelectedReclamation(record);
    setReplyText(record.admin_reply || "");
    setStatusModalVisible(true);
  };

  const handleCloseStatusModal = () => {
    setStatusModalVisible(false);
    setSelectedReclamation(null);
    setReplyText("");
  };

  const handleSendReply = async () => {
    if (!selectedReclamation) return;
    const trimmed = replyText.trim();
    if (!trimmed) {
      message.warning("Veuillez saisir une réponse");
      return;
    }
    setSendingReply(true);
    try {
      await replyToReclamation(selectedReclamation.id, {
        admin_reply: trimmed,
        // Keep the current status; a status change can still be done via the buttons.
        status: selectedReclamation.status,
      });
      message.success("Réponse envoyée au client");
      handleCloseStatusModal();
      fetchReclamations();
    } catch (error) {
      console.error("Error sending reply:", error);
      message.error(
        error.response?.data?.detail || "Échec de l'envoi de la réponse"
      );
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedReclamation) return;
    setUpdatingStatus(true);
    try {
      await updateReclamationStatus(selectedReclamation.id, { status: newStatus });
      message.success("Statut mis à jour avec succès");
      handleCloseStatusModal();
      fetchReclamations();
    } catch (error) {
      console.error("Error updating status:", error);
      message.error(
        error.response?.data?.detail || "Échec de la mise à jour du statut"
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "Type",
      dataIndex: "reclamation_type",
      key: "reclamation_type",
      render: (type) => (
        <Tag color="blue">{reclamationTypeLabels[type] || type}</Tag>
      ),
    },
    {
      title: "Sujet",
      dataIndex: "subject",
      key: "subject",
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={statusColors[status] || "default"}>
          {statusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
    },
    {
      title: "Téléphone",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => phone || "-",
    },
    {
      title: "Date de création",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => new Date(text).toLocaleString("fr-FR"),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewDetail(record)}
          >
            Détails
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleOpenStatusModal(record)}
          >
            Statut
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Gestion des réclamations
            </Title>
            <Tooltip
              title="Vous pouvez modifier le statut d'une réclamation à tout moment via le bouton « Statut ». La réponse envoyée au client peut aussi être modifiée après l'envoi (elle sera mise à jour côté client)."
            >
              <InfoCircleOutlined style={{ color: "#1890ff", cursor: "help" }} />
            </Tooltip>
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Filtrer par Type"
              style={{ width: "100%" }}
              onChange={(value) => handleFilterChange("reclamation_type", value)}
              value={filters.reclamation_type}
              allowClear
            >
              <Option value="infraction">Infraction</Option>
              <Option value="subscription">Abonnement</Option>
              <Option value="other">Autre</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Filtrer par Statut"
              style={{ width: "100%" }}
              onChange={(value) => handleFilterChange("status", value)}
              value={filters.status}
              allowClear
            >
              <Option value="submitted">Soumis</Option>
              <Option value="in_progress">En cours</Option>
              <Option value="resolved">Résolu</Option>
              <Option value="rejected">Rejeté</Option>
              <Option value="closed">Fermé</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              placeholder={["Date de début", "Date de fin"]}
              style={{ width: "100%" }}
              value={dateRange}
              onChange={(dates) => setDateRange(dates || [])}
              format="YYYY-MM-DD"
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                Rechercher
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Réinitialiser
              </Button>
            </Space>
          </Col>
        </Row>
        <div
          style={{ marginBottom: 16, gap: 8 }}
          className="flex justify-end"
        >
          <Button
            icon={<FileExcelOutlined />}
            onClick={handleExcelClick}
            disabled={reclamations.length === 0 || exporting}
          >
            Export Excel
          </Button>
          <Dropdown
            menu={{ items: pdfMenuItems }}
            disabled={reclamations.length === 0 || exporting}
          >
            <Button
              icon={<FilePdfOutlined />}
              disabled={reclamations.length === 0 || exporting}
            >
              Export PDF{" "}
              {exporting ? "..." : <span style={{ marginLeft: 5 }}>▼</span>}
            </Button>
          </Dropdown>
        </div>
        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
        <Table
          dataSource={reclamations}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} réclamations`,
            pageSizeOptions: ["10", "25", "50", "100"],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />

        {/* Detail Modal */}
        <Modal
          title="Détails de la réclamation"
          open={detailModalVisible}
          onCancel={handleCloseDetail}
          footer={[
            <Button key="close" type="primary" onClick={handleCloseDetail}>
              Fermer
            </Button>,
          ]}
          width={700}
        >
          {selectedReclamation && (
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="ID">
                {selectedReclamation.id}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag color="blue">
                  {reclamationTypeLabels[selectedReclamation.reclamation_type] ||
                    selectedReclamation.reclamation_type}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <Tag color={statusColors[selectedReclamation.status] || "default"}>
                  {statusLabels[selectedReclamation.status] ||
                    selectedReclamation.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Prénom">
                {selectedReclamation.first_name}
              </Descriptions.Item>
              <Descriptions.Item label="Nom">
                {selectedReclamation.last_name}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedReclamation.email}
              </Descriptions.Item>
              <Descriptions.Item label="Téléphone">
                {selectedReclamation.phone || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Sujet">
                {selectedReclamation.subject}
              </Descriptions.Item>
              <Descriptions.Item label="Message">
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {selectedReclamation.message}
                </div>
              </Descriptions.Item>
              {selectedReclamation.admin_reply && (
                <Descriptions.Item label="Réponse au client">
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {selectedReclamation.admin_reply}
                  </div>
                  {selectedReclamation.replied_at && (
                    <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                      Répondu le{" "}
                      {new Date(selectedReclamation.replied_at).toLocaleString("fr-FR")}
                    </div>
                  )}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Date de création">
                {new Date(selectedReclamation.created_at).toLocaleString("fr-FR")}
              </Descriptions.Item>
              <Descriptions.Item label="Dernière mise à jour">
                {new Date(selectedReclamation.updated_at).toLocaleString("fr-FR")}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>

        {/* Reply + Status Update Modal */}
        <Modal
          title="Répondre / Modifier le statut"
          open={statusModalVisible}
          onCancel={handleCloseStatusModal}
          footer={null}
          width={480}
        >
          {selectedReclamation && (
            <div>
              <p style={{ marginBottom: 8 }}>
                Réclamation <strong>#{selectedReclamation.id}</strong> — {selectedReclamation.subject}
              </p>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>
                  Réponse au client (visible par le client)
                </div>
                <Input.TextArea
                  rows={4}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Expliquez votre décision au client…"
                  maxLength={2000}
                  showCount
                />
                <Button
                  type="primary"
                  block
                  style={{ marginTop: 8 }}
                  onClick={handleSendReply}
                  loading={sendingReply}
                >
                  Envoyer la réponse
                </Button>
              </div>
              <p style={{ marginBottom: 8 }}>Modifier le statut</p>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  block
                  type={selectedReclamation.status === "submitted" ? "primary" : "default"}
                  onClick={() => handleUpdateStatus("submitted")}
                  loading={updatingStatus}
                >
                  Soumis
                </Button>
                <Button
                  block
                  type={selectedReclamation.status === "in_progress" ? "primary" : "default"}
                  onClick={() => handleUpdateStatus("in_progress")}
                  loading={updatingStatus}
                >
                  En cours
                </Button>
                <Button
                  block
                  type={selectedReclamation.status === "resolved" ? "primary" : "default"}
                  onClick={() => handleUpdateStatus("resolved")}
                  loading={updatingStatus}
                >
                  Résolu
                </Button>
                <Button
                  block
                  type={selectedReclamation.status === "rejected" ? "primary" : "default"}
                  onClick={() => handleUpdateStatus("rejected")}
                  loading={updatingStatus}
                  danger
                >
                  Rejeté
                </Button>
                <Button
                  block
                  type={selectedReclamation.status === "closed" ? "primary" : "default"}
                  onClick={() => handleUpdateStatus("closed")}
                  loading={updatingStatus}
                >
                  Fermé
                </Button>
              </Space>
            </div>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default CitizenReclamationsPage;
